import React, { useEffect, useMemo, useState } from "react";
import { AutoComplete, Avatar, Modal, Segmented, Select, message } from "antd";
import { LockOutlined, NumberOutlined, UserOutlined } from "@ant-design/icons";
import { useChat } from "./ChatContext";
import UserPicker from "./UserPicker";
import { resolveAvatarUrl } from "./zulipClient";
import { CHAT_MODAL_Z_INDEX } from "./types";

// Rough plain-text preview of a message's rendered HTML content — good
// enough for a one-line "what you're about to forward" reminder in this
// modal; not meant to survive as the actual forwarded content (that's the
// raw markdown ChatContext's forwardMessage fetches separately).
function plainTextPreview(html: string, max = 160): string {
  const container = document.createElement("div");
  container.innerHTML = html;
  const text = (container.textContent || "").trim().replace(/\s+/g, " ");
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * "Forward message" destination picker. Unlike Zulip's own webapp — which
 * repurposes the compose box itself, letting the user swap its destination
 * dropdown — this app's Composer always targets the currently open thread,
 * so forwarding to a *different* conversation needs its own destination
 * picker instead: a channel+topic pair (reusing the same subscriptions/
 * streamTopics data ConversationList already loads) or a DM recipient list
 * (reusing UserPicker, same as NewDmModal/InviteMembersModal).
 */
const ForwardMessageModal: React.FC = () => {
  const { forwardTarget, setForwardTarget, forwardMessage, subscriptions, streamTopics, loadTopicsFor, realmUsers } = useChat();
  const [destKind, setDestKind] = useState<"channel" | "dm">("channel");
  const [streamId, setStreamId] = useState<number | undefined>(undefined);
  const [topic, setTopic] = useState("");
  const [userIds, setUserIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Reset the picker fresh each time a new message is targeted, rather than
  // carrying over the previous forward's destination.
  useEffect(() => {
    if (!forwardTarget) return;
    setDestKind("channel");
    setStreamId(undefined);
    setTopic("");
    setUserIds([]);
  }, [forwardTarget]);

  const close = () => setForwardTarget(null);

  const topicOptions = useMemo(
    () => (streamId ? (streamTopics[streamId] ?? []).map((t) => ({ value: t.name })) : []),
    [streamId, streamTopics],
  );

  const handleStreamChange = (id: number) => {
    setStreamId(id);
    setTopic("");
    if (!streamTopics[id]) loadTopicsFor(id);
  };

  const handleOk = async () => {
    if (!forwardTarget) return;
    if (destKind === "channel") {
      const stream = subscriptions.find((s) => s.stream_id === streamId);
      if (!stream) {
        message.warning("Pick a channel first.");
        return;
      }
      if (!topic.trim()) {
        message.warning("Enter a topic name first.");
        return;
      }
      setSubmitting(true);
      try {
        await forwardMessage(forwardTarget, { kind: "channel", stream, topic: topic.trim() });
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (userIds.length === 0) {
      message.warning("Pick at least one person.");
      return;
    }
    const names = realmUsers.filter((u) => userIds.includes(u.user_id)).map((u) => u.full_name);
    setSubmitting(true);
    try {
      await forwardMessage(forwardTarget, { kind: "dm", userIds, label: names.join(", ") || "Direct message" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Forward message"
      open={!!forwardTarget}
      onOk={handleOk}
      onCancel={close}
      confirmLoading={submitting}
      okText="Forward"
      destroyOnClose
      zIndex={CHAT_MODAL_Z_INDEX}
    >
      {forwardTarget && (
        <div className="chat-forward-preview">
          <Avatar size={20} src={resolveAvatarUrl(forwardTarget.avatar_url) || undefined} icon={<UserOutlined />} />
          <div className="chat-forward-preview__body">
            <span className="chat-forward-preview__sender">{forwardTarget.sender_full_name}</span>
            <span className="chat-forward-preview__text">{plainTextPreview(forwardTarget.content)}</span>
          </div>
        </div>
      )}

      <Segmented
        block
        value={destKind}
        onChange={(v) => setDestKind(v as "channel" | "dm")}
        options={[
          { label: "Channel", value: "channel" },
          { label: "Direct message", value: "dm" },
        ]}
        className="chat-forward-segmented"
      />

      {destKind === "channel" ? (
        <>
          <Select
            placeholder="Select a channel…"
            style={{ width: "100%", marginBottom: 8 }}
            value={streamId}
            onChange={handleStreamChange}
            showSearch
            optionFilterProp="label"
            options={subscriptions.map((s) => ({ value: s.stream_id, label: s.name }))}
            optionRender={(option) => {
              const stream = subscriptions.find((s) => s.stream_id === option.value);
              return (
                <span>
                  {stream?.invite_only ? <LockOutlined /> : <NumberOutlined />} {option.label}
                </span>
              );
            }}
          />
          <AutoComplete
            style={{ width: "100%" }}
            placeholder="Topic name…"
            value={topic}
            onChange={setTopic}
            options={topicOptions}
            disabled={!streamId}
            filterOption={(input, option) => String(option?.value ?? "").toLowerCase().includes(input.toLowerCase())}
          />
        </>
      ) : (
        <UserPicker value={userIds} onChange={setUserIds} placeholder="Search people…" />
      )}
    </Modal>
  );
};

export default ForwardMessageModal;
